import { HttpClient, HttpContext } from "@angular/common/http";
import { ApplicationRef, Injectable, inject } from "@angular/core";

import { Observable, from, shareReplay, switchMap, take, tap } from "rxjs";

import { CAH } from "@models";

import { ClientService } from "./client.service";

export enum ESound {
  L_W = "low_win",
  B_W = "big_win",
  S = "start",
  U = "ui",
  K = "kick",
}

export enum EMusic {
  B = "background",
}

@Injectable({ providedIn: "root" })
export class AudioService {
  private readonly hC = inject(HttpClient);
  private readonly aR = inject(ApplicationRef);
  private readonly clS = inject(ClientService);

  private hidden = false;

  /*Volume*/
  private readonly soundVolume = 0.5;
  private readonly musicVolume = 0.6;

  private readonly context: AudioContext = new (window.AudioContext || window.webkitAudioContext)();
  private readonly mNode: GainNode;
  private readonly sNode: GainNode;

  private readonly bufferMap: Record<ESound | EMusic, Observable<AudioBuffer>> = {} as Record<ESound | EMusic, Observable<AudioBuffer>>;
  private readonly sourceMap: Record<ESound | EMusic, AudioBufferSourceNode> = {} as Record<ESound | EMusic, AudioBufferSourceNode>;

  constructor() {
    this.mNode = this.context.createGain();
    this.sNode = this.context.createGain();

    this.mNode.connect(this.context.destination);
    this.sNode.connect(this.context.destination);

    this.mNode.gain.value = this.musicVolume;
    this.sNode.gain.value = this.soundVolume;
  }

  private readonly visibleFn = (): void => {
    this.hidden = document.hidden;

    if (this.hidden) {
      Object.keys(this.sourceMap).forEach(audio => this.stop(audio as ESound | EMusic));
    } else {
      this.aR.tick();

      this.resume();

      this.music(EMusic.B);
    }
  };

  private readonly clickFn = (event: Event): void => {
    const element = event.target as Element | null;
    if (element?.closest?.("[data-ui-sound]")) {
      this.sound(ESound.U);
    }
  };

  public init(): void {
    document.addEventListener("visibilitychange", this.visibleFn);
    document.addEventListener("click", this.clickFn, true);
    this.resume();
  }

  public destroy(): void {
    document.removeEventListener("visibilitychange", this.visibleFn);
    document.removeEventListener("click", this.clickFn, true);
  }

  public sound(sound?: ESound): void {
    if (!sound || this.clS.sF$().sound_off || this.clS.stop$() || this.hidden) {
      return;
    }

    this.play(sound);
  }

  public music(music?: EMusic): void {
    if (!music || this.clS.sF$().music_off || this.clS.stop$() || this.hidden) {
      return;
    }

    this.play(music);
  }

  public stop(sound: ESound | EMusic): void {
    this.sourceMap[sound] && this.sourceMap[sound].stop();
  }

  private play(sound: EMusic | ESound): void {
    this.loadAudio(sound)
      .pipe(
        take(1),
        tap(buffer => {
          this.stop(sound);

          const loop = sound === EMusic.B;
          const source = this.context.createBufferSource();

          source.buffer = buffer;
          source.loop = loop;
          source.connect(loop ? this.mNode : this.sNode);
          source.start();

          this.sourceMap[sound] = source;
        }),
      )
      .subscribe();
  }

  private loadAudio(source: EMusic | ESound): Observable<AudioBuffer> {
    if (!this.bufferMap[source]) {
      this.bufferMap[source] = this.hC
        .get(`/assets/sounds/${source}.mp3`, {
          responseType: "arraybuffer",
          context: new HttpContext().set(CAH, false),
        })
        .pipe(
          switchMap(buffer => from(this.context.decodeAudioData(buffer))),
          shareReplay(1),
        );
    }

    return this.bufferMap[source];
  }

  private resume(): void {
    document.addEventListener(
      "pointerup",
      () => {
        if (this.context.state === "suspended") {
          this.context.resume();
        }
      },
      { once: true },
    );
  }
}
